// Some additional reactivity helpers for Alpine.js, inspired by Solid.js

import { equals } from '@js/util/helpers.js'
import Alpine from 'alpinejs'

let symbol = Symbol('signal marker')

/**
 * Create a signal
 *
 * @template T
 * @param {T} initialValue
 * @returns {[() => T, (newValue: T) => void]}
 *
 * @example
 * ```js
 * let [count, setCount] = signal(0)
 * console.log('count:', count()) // count: 0
 * ```
 */
export function signal(initialValue) {
  let data = Alpine.reactive({ value: initialValue })
  let getter = () => data.value
  getter[symbol] = true

  return [
    getter,
    newValue => {
      data.value = newValue
    },
  ]
}

/**
 * Create a derived signal (like Vue computed)
 *
 * @template T
 * @param {() => T} callback
 * @returns {() => T}
 *
 * @example
 * ```js
 * let [count, setCount] = signal(0)
 * let squared = derived(() => count() ** 2)
 * console.log('square:', squared()) // count: 0
 * setCount(2)
 * console.log('square:', squared()) // count: 4
 * ```
 */
export function derived(callback) {
  let [memoized, setMemoized] = signal(callback())

  Alpine.effect(() => {
    let newValue = callback()
    if (equals(memoized(), newValue)) return
    setMemoized(newValue)
  })

  return memoized
}

/**
 * Check if a value is a signal
 *
 * @param {any} value
 * @returns {boolean}
 */
export function isSignal(value) {
  return value?.[symbol] === true
}

/**
 * Deeply unwrap a signal
 */
export function unwrap(value) {
  return isSignal(value) ? unwrap(value()) : value
}

/**
 * Watch a signal
 *
 * @template T
 * @param {() => T} signal
 * @param {(newValue: T, oldValue: T|undefined) =>} callback
 * @param {{ immediate?: boolean }} options
 *
 * @example
 * ```js
 * let [location, setLocation] = signal('home')
 * watch(count, (newValue, oldValue) => {
 *   console.log('go from %s to %s', oldValue, newValue)
 * })
 * setLocation('work') // 'go from home to work'
 * setLocation('work') // nothing logged
 * setLocation('home') // 'go from work to home'
 * ```
 */
export function watch(signal, callback, { immediate = false } = {}) {
  let memoized = derived(() => signal())

  let first = true
  let oldValue = undefined
  Alpine.effect(() => {
    let newValue = memoized()

    if (first) {
      if (immediate) {
        queueMicrotask(() => {
          callback(newValue, oldValue)
          oldValue = newValue
        })
      } else {
        oldValue = newValue
      }

      first = false
    } else {
      if (newValue === oldValue) return

      queueMicrotask(() => {
        callback(newValue, oldValue)
        oldValue = newValue
      })
    }
  })
}

/**
 * Check whether an element contains another element
 *
 * @param {Element} possibleContainer
 * @param {Element} possibleChild
 */
function useContains(possibleContainer, possibleChild) {
  let [contains, setContains] = signal(possibleContainer.contains(possibleChild))

  let observer = new MutationObserver(mutations => {
    for (let mutation of mutations) {
      let affectedNodes = [...mutation.addedNodes, ...mutation.removedNodes]

      if (
        affectedNodes.includes(possibleChild) ||
        affectedNodes.some(affectedNode => affectedNode.contains(possibleChild))
      ) {
        setContains(possibleContainer.contains(possibleChild))
      }
    }
  })

  observer.observe(document, {
    childList: true,
    subtree: true,
  })

  return [
    contains,
    {
      stop() {
        observer.disconnect()
      },
    },
  ]
}

let providers = Alpine.reactive(new Map())

/**
 * Provide a value to a DOM subtree
 *
 * @template T
 * @param {Element} el
 * @param {string|symbol} key
 * @param {T} value
 * @returns {() => void}
 */
export function provide(el, key, value) {
  let data
  if (providers.has(key)) {
    data = providers.get(key)
  } else {
    data = new Map()
    providers.set(key, data)
  }

  data.set(el, value)

  return () => {
    data.delete(el)
  }
}

/**
 * Inject a signal from the closest provider
 *
 * @template T
 * @param {Element} el
 * @param {string|symbol} key
 * @returns {() => T}
 *
 * @example
 * ```js
 * let data = inject(someElement, 'key')
 *
 * // watch injected signal for changes
 * Alpine.effect(() => { console.log(data()) })
 *
 * // logs 'undefined'
 *
 * let unprovideBody = provide(document.body, 'key', 'value')
 * // logs 'value'
 *
 * provide(document.documentElement, 'key', 'other value')
 * // logs nothing since <body> is closer than <html>
 *
 * unprovideBody()
 * // logs 'other value'
 * ```
 */
export function inject(el, key) {
  let stoppers = []

  return derived(() => {
    let providingElements = [...(providers.get(key)?.keys() ?? [])]

    for (let stopper of stoppers) stopper()

    stoppers.splice(0)

    // TODO: stop mutation observers
    let matchingElements = providingElements.filter(providingElement => {
      let [matches, { stop }] = useContains(providingElement, el)
      stoppers.push(stop)
      return matches()
    })

    matchingElements.sort((a, b) =>
      a.compareDocumentPosition(b) === Node.DOCUMENT_POSITION_CONTAINS ? -1 : 1,
    )

    let closestContainingElement = matchingElements.at(-1)

    return unwrap(providers.get(key)?.get(closestContainingElement))
  })
}
